import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@web/components/ui/card';
import { trpc } from '@web/lib/trpc';
import { Button } from '@web/components/ui/button';
import { DollarSign, Link, TrashIcon, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';

import { Route } from '@web/routes/dashboard/route';

import { ContractStatusBadge } from './contract-status-badge';

export function ContactContracts({ contactId }: { contactId: string }) {
  const { setActiveContractId, setActivePaymentContractId } =
    Route.useRouteContext();

  const {
    contracts: { listAll: contractsUtils },
  } = trpc.useUtils();

  const { data: contractsData, refetch: refetchContractsData } =
    trpc.contracts.listAll.useQuery({
      contactId,
    });

  const { mutateAsync: deleteContractByIdAsync } =
    trpc.contracts.deleteContractById.useMutation();

  const { mutateAsync: sendPaymentLinkAsync } =
    trpc.payment.sendPaymentLink.useMutation();

  const { mutateAsync: createNewContractAsync } =
    trpc.contracts.createContractWithDefaults.useMutation();

  trpc.contracts.onContractStatusUpdated.useSubscription(
    {},

    {
      onData: (update) => {
        contractsUtils.setData({ contactId }, (prevData) => {
          if (!prevData) return [];

          return prevData.map((contract) =>
            update.id === contract.id
              ? {
                  ...contract,
                  status: update.status,
                }
              : contract,
          );
        });
      },
    },
  );

  trpc.contracts.onContractDeleted.useSubscription(
    {},

    {
      onData: (update) => {
        contractsUtils.setData({ contactId }, (prevData) => {
          if (!prevData) return [];

          return prevData.filter((contract) => contract.id !== update.id);
        });
      },
    },
  );

  if (!contractsData) return null;

  const handleDeleteContract = async (contractId: string) => {
    await deleteContractByIdAsync({ contractId });
    toast.success('Contract deleted');
    await refetchContractsData();
  };

  const handleCreateNewContract = async () => {
    if (!contactId) return;

    const newContract = await createNewContractAsync({
      contactId,
    });

    if (!newContract) return;

    toast.success('New contract created');
    setActiveContractId(newContract.contractId);
    await refetchContractsData();
  };

  return (
    <div className="space-y-4">
      <div className="mb-3 flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          className="bg-primary/90 text-primary-foreground hover:bg-primary/90 h-8 w-full rounded-full px-3"
          onClick={() => void handleCreateNewContract()}
        >
          <PlusCircle className="mr-1 h-4 w-4" />
          New Contract
        </Button>
      </div>

      {contractsData.length === 0 ? (
        <div className="rounded-md border border-gray-100 bg-gray-50 p-4 text-center">
          <p className="text-sm text-gray-500">No contracts yet</p>
          <p className="mt-1 text-xs text-gray-400">
            Click &quot;New Contract&quot; to create one
          </p>
        </div>
      ) : (
        contractsData.map((contract) => (
          <Card
            key={contract.id}
            className="bg-card relative cursor-pointer p-0"
            onClick={() => {
              console.log('contract', contract);
              setActiveContractId(contract.id);
            }}
          >
            <CardHeader className="p-0">
              <CardTitle className="flex flex-row justify-between p-2 text-sm font-medium leading-none">
                <div className="text-muted-foreground text-sm">
                  <ContractStatusBadge status={contract.status} />
                </div>

                <div className="flex gap-2">
                  <Button
                    className="h-min w-min bg-blue-500 px-1 py-1"
                    variant="default"
                    size="icon"
                    onClick={(e) => {
                      void sendPaymentLinkAsync({ contractId: contract.id });
                      e.stopPropagation();
                    }}
                  >
                    <Link className="size-3" />
                  </Button>

                  <Button
                    className="h-min w-min px-1 py-1"
                    variant="default"
                    size="icon"
                    onClick={(e) => {
                      setActivePaymentContractId(contract.id);
                      e.stopPropagation();
                    }}
                  >
                    <DollarSign className="size-3" />
                  </Button>

                  {contract.status === 'draft' && (
                    <Button
                      className="h-min w-min px-1 py-1"
                      variant="destructive"
                      size="icon"
                      onClick={(e) => {
                        void handleDeleteContract(contract.id);
                        e.stopPropagation();
                      }}
                    >
                      <TrashIcon className="size-3" />
                    </Button>
                  )}
                </div>
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-1 px-3 pt-3">
              <p className="text-sm">{contract.name}</p>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
